import { ArrowRight, Box, Lock } from 'lucide-react';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { definitionModule, type Module, type PalierModule } from '@artosera/shared';
import { ICONES_MODULES } from './catalogue.js';
import styles from './TuileModule.module.css';

export interface TuileModuleProps {
  readonly module: Module;
  readonly galerieSlug: string;
  /**
   * `avenir` : module d'un palier verrouillé — lien vers sa fiche, jamais vers
   * son écran. `actif` : module que la galerie a ; `to` est sa page, absent
   * quand il n'en a pas.
   */
  readonly etat: 'actif' | 'avenir';
  readonly palier?: Exclude<PalierModule, 'mvp'>;
  readonly to?: string | null;
  /** Module actif consultable en lecture seule : cadenas et « Lecture seule », il s'ouvre. */
  readonly lectureSeule?: boolean;
}

/**
 * Une tuile de la carte des modules (maquette Halo « cartes par palier »). Un
 * module à venir se lit sans la couleur : cadenas, texte « Arrive en V2 » et
 * contour en tirets. Ce n'est jamais présenté comme une erreur.
 */
export function TuileModule({
  module,
  galerieSlug,
  etat,
  palier,
  to,
  lectureSeule = false,
}: TuileModuleProps): JSX.Element {
  const { t } = useTranslation(['regie', 'commun']);
  const Icone = ICONES_MODULES[module] ?? Box;
  const nom = t(definitionModule(module).libelle);
  const verrouille = etat === 'avenir' && palier !== undefined;

  const corps = (
    <>
      <span className={styles.icone} aria-hidden="true">
        <Icone size={18} strokeWidth={1.5} />
      </span>
      <span className={styles.corps}>
        <span className={styles.nom}>{nom}</span>
        <span className={styles.meta}>
          {verrouille ? (
            <>
              <Lock size={14} strokeWidth={1.5} aria-hidden />
              {t(`regie:modulesAVenir.arrive.${palier}`)}
            </>
          ) : lectureSeule ? (
            <>
              <Lock size={14} strokeWidth={1.5} aria-hidden />
              {t('regie:module.lectureSeule.etiquette')}
            </>
          ) : to !== null && to !== undefined ? (
            <>
              {t('regie:modulesAVenir.carte.ouvrir')}
              <ArrowRight size={14} strokeWidth={1.5} aria-hidden />
            </>
          ) : (
            t('regie:modulesAVenir.carte.actif')
          )}
        </span>
      </span>
      {verrouille ? (
        <span className={styles.palier} aria-hidden="true">
          {t(`regie:modulesAVenir.palier.${palier}`)}
        </span>
      ) : null}
    </>
  );

  const classe = [styles.tuile, verrouille ? styles.verrouillee : styles.active].join(' ');

  if (verrouille) {
    return (
      <Link
        className={classe}
        to={`/${galerieSlug}/modules/${module}`}
        data-module={module}
        data-etat="avenir"
      >
        {corps}
      </Link>
    );
  }
  if (to !== null && to !== undefined) {
    return (
      <Link className={classe} to={to} data-module={module} data-etat="actif">
        {corps}
      </Link>
    );
  }
  return (
    <div className={`${classe} ${styles.statique}`} data-module={module} data-etat="actif">
      {corps}
    </div>
  );
}
